import { Json } from '@se/cp.common';
import { DataBinding } from '@se/se.platform.base';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { ICpEntity, ICpEntityFactory, isCpEntityList, PropertyType, PropertyVisitor } from './interfaces';

export function isEntity(value: any): value is ICpEntity {
    return value instanceof CpEntityBase;
}

export function isArrayOfEntities(value: any): value is ICpEntity[] {
    if (value instanceof Array) {
        if (value.length > 0) {
            return value.every((e) => isEntity(e));
        }
    }

    return false;
}

export abstract class CpEntityBase implements ICpEntity {
    // Subject in cui teniamo le proprietà in errore.
    protected propertiesInError: Record<string, Record<string, boolean>> = {};
    protected readonly propertiesInErrorChanged = new BehaviorSubject<void>(undefined);

    protected readonly isValidSubject = new BehaviorSubject<boolean>(true);

    protected readonly isModifiedSubject = new BehaviorSubject<boolean>(false);

    protected readonly autoUpdatedPropertyListSubject = new BehaviorSubject<Record<string, boolean>>({});

    protected constructor(
        protected readonly factory: ICpEntityFactory,
        readonly path: DataBinding,
        readonly parent?: ICpEntity,
    ) {}

    // #region Helpers

    abstract navigateToPath(path: DataBinding, createMissingEntities?: boolean): PropertyType | undefined;

    // #endregion

    // #region ICpEntity implementation

    abstract Id: string;

    abstract addEntityToList(path: DataBinding, value: PropertyType): void;

    abstract forOwn(fn: PropertyVisitor): void;

    abstract getSelectedEntity(path: DataBinding): ICpEntity | undefined;

    abstract getAllEntities(path: DataBinding): ICpEntity[] | undefined;

    abstract getPropertyPrimitiveValue(propertyName: string): PropertyType;

    abstract getPropertyValue(propertyName: string): PropertyType;

    abstract hasProperty(propertyName: string): boolean;

    abstract remove(): boolean;

    abstract select(): void;

    abstract setPropertyValue(propertyName: string, value: PropertyType, emitEvent?: boolean): void;

    abstract toJSON(skipAutogeneratedId?: boolean): Json;

    get isModified() {
        return this.isModifiedSubject.value;
    }

    get isModified$() {
        return this.isModifiedSubject.asObservable().pipe(distinctUntilChanged());
    }

    set isModified(isModified: boolean) {
        this.isModifiedSubject.next(isModified);
        if (this.parent && this.parent.isModified !== isModified) {
            this.parent.isModified = isModified;
        }
    }

    get isValid$() {
        return this.isValidSubject.asObservable().pipe(distinctUntilChanged());
    }

    get isValid() {
        return this.isValidSubject.value;
    }

    get autoUpdatedPropertyList$() {
        return this.autoUpdatedPropertyListSubject.asObservable().pipe(distinctUntilChanged());
    }

    get isAutoUpdated() {
        return !!Object.values(this.autoUpdatedPropertyListSubject.value).find((item) => item);
    }

    addAutoUpdated(propertyName: string | undefined, autoUpdated: boolean) {
        if (!propertyName) return;
        this.autoUpdatedPropertyListSubject.next({
            ...this.autoUpdatedPropertyListSubject.value,
            [propertyName]: autoUpdated,
        });
    }

    flushAutoUpdated() {
        this.autoUpdatedPropertyListSubject.next({});
    }

    flushAutoUpdatedRecursive() {
        this.forOwn((_propName, propValue) => {
            if (isCpEntityList(propValue) || isEntity(propValue)) {
                propValue.flushAutoUpdatedRecursive();
            }
        });
        this.flushAutoUpdated();
    }

    destroy() {
        this.clearErrorsBeforeDestroy();
        this.isModifiedSubject.complete();
        this.isValidSubject.complete();
        this.propertiesInErrorChanged.complete();
        this.autoUpdatedPropertyListSubject.complete();
    }

    getValue(path: DataBinding): PropertyType {
        if (path.isEmpty()) {
            return this;
        }
        if (path.property.isAdvanced()) {
            return this.navigateToPath(path, false) ?? null;
        }

        const entityPath = path.entity;
        const propertyPath = path.propertyName;
        const entity = this.navigateToPath(entityPath, false);
        if (!entity || this.factory.isUndoEntity(entity)) {
            return null;
        }

        if (!isEntity(entity)) {
            throw new Error(`Value at ${path} is not an entity.`);
        }

        if (!propertyPath) {
            return entity;
        }

        return entity.getPropertyValue(propertyPath);
    }

    getNestedPropertyValue(commaSeparatedPropertyName: string): PropertyType {
        if (!commaSeparatedPropertyName) {
            return null;
        }
        const prop = this.getPropertyValue(commaSeparatedPropertyName.split('.')[0]);
        const next = commaSeparatedPropertyName.split('.').slice(1).join('.');
        if (!next) {
            return prop;
        }
        if (isEntity(prop)) {
            return prop.getNestedPropertyValue(next);
        }
        return prop;
    }

    setValue(path: DataBinding, value: PropertyType, emitEvent?: boolean, autoUpdated?: boolean) {
        const entityPath = path.entity;

        const propertyPath = path.propertyName;
        const entity = this.navigateToPath(entityPath, false);
        if (!isEntity(entity)) {
            console.warn(`Value at ${path} is not an entity.`);
            return;
        }
        entity.addAutoUpdated(propertyPath, Boolean(autoUpdated));
        entity.setPropertyValue(propertyPath, value, emitEvent);
    }

    getEntity(entityPath: DataBinding, createMissingEntities = false): ICpEntity | undefined {
        const entity = this.navigateToPath(entityPath, createMissingEntities);
        if (!entity || this.factory.isUndoEntity(entity)) {
            return undefined;
        }
        if (isEntity(entity)) {
            return entity;
        }

        throw new Error(`Value at ${entityPath} is not an entity.`);
    }

    // #endregion

    getPropertyErrorStatus(propertyName: string, validationName?: string) {
        return this.isPropertyInError(propertyName, validationName);
    }

    propertyErrorStatus(propertyName: string, validationName?: string) {
        return this.propertiesInErrorChanged.pipe(
            map(() => {
                return this.isPropertyInError(propertyName, validationName);
            }),
            distinctUntilChanged(),
        );
    }

    setPropertyErrorStatus(propertyName: string, validationName: string, isValid: boolean) {
        let errors = this.propertiesInError[propertyName] ?? {};

        const hasThisError = errors[validationName];

        if ((isValid && !hasThisError) || (!isValid && hasThisError)) {
            return;
        }

        errors[validationName] = !isValid;
        this.propertiesInError[propertyName] = errors;

        this.refreshValidityStatus(validationName);

        this.propertiesInErrorChanged.next();
    }

    refreshValidityStatus(validationName: string) {
        let isValid = true;

        // Basta che una proprietà sia in errore per mandare in errore la entity.
        for (const property in this.propertiesInError) {
            if (Object.hasOwnProperty.call(this.propertiesInError, property)) {
                isValid &&= !this.isPropertyInError(property);
            }
        }

        if (isValid !== this.isValid) {
            this.isValidSubject.next(isValid);
            if (this.parent && this.parent.isValid !== isValid && this.parent.setPropertyErrorStatus) {
                this.parent.setPropertyErrorStatus(this.path.propertyName, validationName, isValid);
            }
        }
    }

    updatePath(): string {
        const path = [];
        let current: ICpEntity = this;
        let parent = current.parent;
        let name = current.path?.property?.value.toString() || '';
        if (!parent) return name || '';

        while (parent) {
            const parentName = parent.path?.property?.value.toString() || '';

            if (isCpEntityList(parent)) {
                const index = parent.getEntityIndex(current);
                path.push(index);
                /*in questo caso il name dovrebbe corrispondere all'indice*/
            } else {
                path.push(name);
            }

            name = parentName;
            current = parent;
            parent = current.parent;
        }
        return path.reverse().join('.');
    }

    private isPropertyInError(property: string, validationName?: string) {
        const errors = this.propertiesInError[property];
        if (!errors) {
            return false;
        }

        if (validationName) {
            return !!errors[validationName];
        }

        for (const error in errors) {
            if (Object.hasOwnProperty.call(errors, error) && errors[error]) {
                return true;
            }
        }

        return false;
    }

    /** funzione per ripulire la entity dagli errori prima della distruzione dell'istanza per propagare l'informazione ai padri */
    private clearErrorsBeforeDestroy() {
        Object.values(this.propertiesInError).forEach((property) => {
            Object.keys(property).forEach((key) => {
                property[key] = false;
                this.refreshValidityStatus(key);
            });
        });
    }
}
